import {
  Button,
  DropdownMenu,
  Flex,
  Grid,
  Progress,
  Select,
  Text,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import { useQuery, useQueryClient } from "@tanstack/react-query";
// eslint-disable-next-line import/named
import { createRoute, Navigate } from "@tanstack/react-router";
import { Label } from "radix-ui";
import { useReducer, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { AvatarWithInitials } from "../../../components/AvatarWithInitials";
import EnterpriseSwitcher from "../../../components/EnterpriseSwitcher";
import { getGuidelines } from "../../../services/brand-guidelines";
import { getFolderTree } from "../../../services/folders";
import { getProject } from "../../../services/projects";
import { errorAnalyticsPayload, useAnalytics } from "../../../utils/analytics";
import {
  folderUrlForFigmaChip,
  ticketUrlForFigmaChip,
} from "../../../utils/figma-plugin";
import {
  type FigmaMessage,
  MessageType,
} from "../../../utils/figma-plugin-types";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { assetForUser } from "../../../utils/imageHandler";
import {
  isFromExpectedParent,
  postToParent,
} from "../../../utils/parent-messaging";
import {
  useProjectMutation,
  useProjects,
} from "../../../utils/queries/projects";
import { logout } from "../../../utils/slices/auth";
import { authLayoutRoute } from "../../auth-layout/route";
import { loginRoute } from "../../login/route";
import { useUploadRevision } from "../components/useUploadRevision";

type ActionType =
  // user has just chosen a ticket from the dropdown
  | "select_ticket"
  // user has just selected or deselected a frame in Figma (may cause view
  // switching)
  | "selection_change"
  // user has opted to create a ticket and we need to swap the view
  | "create_ticket"
  // user has given the new ticket a name or updated the current name
  | "name_ticket"
  // user has initated the upload and optionally ticket creation process
  | "ready"
  // there was an error in the process
  | "error"
  // the process has completed
  | "complete"
  // user has reset the flow
  | "reset";

type Status =
  | "select_ticket"
  | "create_ticket"
  | "ready"
  | "complete"
  | "error";

type FigmaFlowState = {
  status: Status;
  selection: boolean;
  newTicketName: string;
  ticketId?: string;
  folderId?: string;
  url?: string;
  errorMessage?: string | null;
};

type FigmaFlowAction = { type: ActionType; payload: Partial<FigmaFlowState> };

const initialReducerState: FigmaFlowState = {
  errorMessage: null,
  newTicketName: "",
  selection: false,
  status: "create_ticket",
  ticketId: undefined,
};

function checkSelection() {
  postToParent({
    type: MessageType.FRAME_LOADED,
    sender: "web",
    message: "true",
  });
}

function flowStateReducer(
  state: FigmaFlowState,
  action: FigmaFlowAction,
): FigmaFlowState {
  const { type, payload } = action;

  switch (type) {
    case "ready":
      return { ...state, status: "ready" };
    case "select_ticket":
      checkSelection();
      return {
        ...state,
        ticketId: payload.ticketId,
        folderId: payload.folderId,
        status:
          Object.keys(payload).length === 0 ? "select_ticket" : state.status,
      };
    case "selection_change":
      return { ...state, selection: payload.selection! };
    case "create_ticket":
      checkSelection();
      return { ...state, status: "create_ticket" };
    case "name_ticket":
      return { ...state, newTicketName: payload.newTicketName! };
    case "error":
      return { ...state, errorMessage: payload.errorMessage };
    case "complete":
      return { ...state, status: "complete", url: payload.url };
    case "reset":
      return { ...initialReducerState, selection: state.selection };
    default:
      throw new Error("Unknown reducer action type");
  }
}

export const figmaPluginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "tickets/figma-plugin",
  component: function Component() {
    const { t } = useTranslation("ui");
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.auth.value);
    const queryClient = useQueryClient();
    const tickets = useProjects();
    const ticketMutation = useProjectMutation();
    const { upload, reset } = useUploadRevision();
    const posthog = useAnalytics();

    const folderTree = useQuery({
      queryKey: ["folders", "tree"],
      queryFn: getFolderTree,
    });

    const guidelinesQuery = useQuery({
      queryKey: ["brand-guidelines"],
      queryFn: getGuidelines,
    });
    const aiPersonas = (guidelinesQuery.data?.aiPersonas ?? [])
      .filter((p) => p.default)
      .map((p) => p.name);

    const [state, dispatchState] = useReducer(
      flowStateReducer,
      initialReducerState,
    );
    // Check selection once when component mounts
    useEffect(() => {
      checkSelection();
    }, []);

    // Add a new useEffect to handle ticket fetching errors
    useEffect(() => {
      if (tickets.isError) {
        console.error("Error fetching tickets:", tickets.error);
        posthog.capture(
          "figma_plugin_tickets_loading_error",
          errorAnalyticsPayload(tickets.error),
        );
        dispatch(logout(figmaPluginRoute.to));
      }
    }, [tickets.isError, tickets.error, dispatch]);

    useEffect(() => {
      async function handleFigmaFrameMessage(event: MessageEvent) {
        // Only the frame that embeds the app may drive it. Without this an
        // unexpected embedder has no way to receive our messages but can still
        // send one: a SEND_FIGMA_SELECTION runs the upload below.
        if (!isFromExpectedParent(event)) return;

        const data: FigmaMessage = event.data;

        // Sender = figma; type = GET_FIGMA_SELECTION
        // This is different than if the web were to make the same request.
        if (data.type === MessageType.GET_FIGMA_SELECTION) {
          if (data.message === "SELECTION") {
            dispatchState({
              type: "selection_change",
              payload: { selection: true },
            });
          } else if (data.message === "NONE") {
            dispatchState({
              type: "selection_change",
              payload: { selection: false },
            });
          }
        }

        // We requested the selected frames as images and Figma responded as
        // follows
        if (data.type === MessageType.SEND_FIGMA_SELECTION) {
          const { images } = data;

          if (!images) {
            console.error("There are no images");
            return;
          }

          const startUpload = performance.now();
          const success = await upload(images, state.ticketId);
          const uploadDuration = performance.now() - startUpload;

          if (success) {
            const startFetch = performance.now();
            const ticket = await queryClient.fetchQuery({
              queryKey: ["tickets", state.ticketId],
              queryFn: () => getProject(state.ticketId!),
              staleTime: 0, // Re-fetch ticket since we just modified it
            });
            const fetchDuration = performance.now() - startFetch;

            posthog.capture("figma_plugin_upload_success", {
              upload_duration_ms: uploadDuration,
              fetch_duration_ms: fetchDuration,
              ticket_id: state.ticketId,
              ticket_name: ticket.title,
              folder_id: state.folderId,
              num_images: images.length,
            });

            let url: string;
            if (state.folderId) {
              url = folderUrlForFigmaChip(state.folderId, aiPersonas);
            } else {
              // The upload should be the most recent version, which opens by default
              url = ticketUrlForFigmaChip(ticket._id);
            }
            postToParent({
              type: MessageType.UPLOAD_SUCCESS,
              sender: "web",
              message: url,
            });

            dispatchState({ type: "complete", payload: { url } });
          }
        }
      }

      const resizeObserver = new ResizeObserver((entries) => {
        const newSize = String(Math.max(entries[0].target.clientHeight, 200));
        const message: FigmaMessage = {
          type: MessageType.RESIZE,
          sender: "web",
          message: newSize,
        };

        postToParent(message);
      });

      window.addEventListener("message", handleFigmaFrameMessage);
      const observedEl = document.body.querySelector("main");

      if (observedEl) {
        resizeObserver.observe(observedEl);
      }

      return () => {
        window.removeEventListener("message", handleFigmaFrameMessage);
        if (observedEl) {
          resizeObserver.unobserve(observedEl);
        }
      };
    }, [queryClient, state, upload]);

    function requestFigmaSelection() {
      postToParent({
        type: MessageType.GET_FIGMA_SELECTION,
        sender: "web",
      } as FigmaMessage);
    }

    function renderView() {
      if (state.status === "error") {
        return (
          <Grid gap="2">
            <Text>{state.errorMessage}</Text>

            <Button
              size="1"
              variant="soft"
              color="gray"
              data-testid="figma-ticket-error-reset-button"
              onClick={() => dispatchState({ type: "reset", payload: {} })}
            >
              {t("common:actions.reset")}
            </Button>
          </Grid>
        );
      }

      if (state.status === "create_ticket") {
        return (
          <Grid gap="3" className="dark">
            <Label.Root htmlFor="figma-ticket-name">
              <Text size="3" className="text-base-white">
                {t("tickets.figma-ticket.newTicketName")}
              </Text>

              <TextField.Root
                id="figma-ticket-name"
                size="2"
                value={state.newTicketName}
                placeholder={t("tickets.figma-ticket.enterName")}
                onChange={({ target }) =>
                  dispatchState({
                    type: "name_ticket",
                    payload: { newTicketName: target.value },
                  })
                }
              />
            </Label.Root>

            {folderTree.data && folderTree.data.length > 0 && (
              <Label.Root htmlFor="figma-ticket-folder">
                <Text size="3" className="block text-base-white">
                  {t("tickets.figma-ticket.selectFolder")}
                </Text>

                <Select.Root
                  defaultValue="none"
                  onValueChange={(value) => {
                    dispatchState({
                      type: "select_ticket",
                      payload: {
                        folderId:
                          value === "none" ? undefined : value || undefined,
                      },
                    });
                  }}
                >
                  <Select.Trigger
                    id="figma-ticket-folder"
                    placeholder={t("tickets.figma-ticket.selectFolder")}
                    className="w-full"
                  />
                  <Select.Content
                    id="figma-ticket-folder"
                    className="dark max-w-[284px]"
                  >
                    <Select.Item value="none">
                      {t("common:labels.none")}
                    </Select.Item>
                    <Select.Separator />
                    {[...(folderTree.data ?? [])]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((folder) => {
                        const renderFolder = (f: typeof folder) => (
                          <>
                            <Select.Item
                              key={f._id}
                              value={f._id!}
                              className="h-auto min-h-[var(--select-item-height)] max-w-[268px] break-all py-1"
                            >
                              {f.name}
                            </Select.Item>
                            {f.folders && f.folders.length > 0 && (
                              <Select.Group className="ml-4">
                                {[...f.folders]
                                  .sort((a, b) => a.name.localeCompare(b.name))
                                  .map(renderFolder)}
                              </Select.Group>
                            )}
                          </>
                        );
                        return renderFolder(folder);
                      })}
                  </Select.Content>
                </Select.Root>
              </Label.Root>
            )}

            <Tooltip
              content={
                !state.selection
                  ? "Please select one or more frames first"
                  : !state.newTicketName.trim().length
                    ? "Please enter a name"
                    : "Submit" // It'd be nice to hide the tooltip instead
              }
              side="top"
              delayDuration={200}
            >
              {/*
                Native disabled buttons don't fire pointer events, so Radix
                Tooltip.Trigger (asChild on the Button) never opens. Wrap in a
                focusable span and disable pointer-events on the button when
                disabled — same pattern as Radix docs for disabled tooltips.
              */}
              <span
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focusable wrapper surfaces the tooltip for the disabled button (keyboard a11y; Radix disabled-trigger pattern)
                tabIndex={0}
                className="inline-flex w-full"
                data-testid="figma-ticket-upload-assets-tooltip-trigger"
              >
                <Button
                  variant="solid"
                  size="2"
                  className={
                    !state.selection || !state.newTicketName.trim().length
                      ? "pointer-events-none w-full"
                      : "w-full"
                  }
                  data-testid="figma-ticket-upload-assets-button"
                  disabled={
                    !state.selection || !state.newTicketName.trim().length
                  }
                  onClick={async () => {
                    dispatchState({ type: "ready", payload: {} });

                    const startTime = performance.now();
                    const folder = state.folderId;

                    ticketMutation
                      .mutateAsync({ title: state.newTicketName, folder })
                      .then((response) => {
                        // reload the tickets for the dropdown
                        tickets.refetch();

                        posthog.capture("figma_plugin_create_ticket", {
                          ticket_name: state.newTicketName,
                          ticket_id: response._id,
                          folder_id: folder,
                          duration_ms: performance.now() - startTime,
                        });

                        dispatchState({
                          type: "select_ticket",
                          payload: {
                            ticketId: response._id,
                            folderId: folder,
                          },
                        });

                        requestFigmaSelection();
                      });
                  }}
                >
                  {t("tickets.figma-ticket.uploadAssets")}
                </Button>
              </span>
            </Tooltip>

            <Flex gap="2" align="center" justify="center">
              <Text size="3" className="text-base-white" align="center" m="2">
                or
              </Text>

              <Button
                size="1"
                variant="outline"
                color="gray"
                className="max-w-max"
                data-testid="figma-ticket-select-existing-button"
                onClick={() =>
                  dispatchState({ type: "select_ticket", payload: {} })
                }
              >
                {t("tickets.figma-ticket.selectExistingTicket")}
              </Button>
            </Flex>
          </Grid>
        );
      }

      if (state.status === "select_ticket") {
        return (
          <Grid gap="3" className="dark">
            <Label.Root htmlFor="figma-ticket-select">
              <Text size="3" className="text-base-white">
                {t("tickets.figma-ticket.selectAsset")}
              </Text>

              <Select.Root
                size="2"
                value={state.ticketId}
                onValueChange={(value) => {
                  postToParent({
                    type: MessageType.FRAME_LOADED,
                    sender: "web",
                    message: "true",
                  });
                  dispatchState({
                    type: "select_ticket",
                    payload: { ticketId: value },
                  });
                  posthog.capture("figma_plugin_select_ticket", {
                    ticket_id: value,
                  });
                }}
              >
                <Select.Trigger
                  id="figma-ticket-select"
                  className="w-full"
                  variant="surface"
                  placeholder={
                    tickets.isPending
                      ? "Loading assets..."
                      : "Search or Select Asset"
                  }
                />
                <Select.Content className="dark max-w-[284px]">
                  {tickets.data?.tickets.map((ticket) => (
                    <Select.Item
                      key={ticket._id}
                      value={ticket._id}
                      className="h-auto min-h-[var(--select-item-height)] max-w-[268px] break-all py-1"
                    >
                      {ticket.title}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Label.Root>

            <Tooltip
              content={
                !state.selection
                  ? "Please select one or more frames first"
                  : !state.ticketId
                    ? "Please select an asset first"
                    : undefined
              }
              side="bottom"
              delayDuration={200}
            >
              <span
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focusable wrapper surfaces the tooltip for the disabled button (keyboard a11y; Radix disabled-trigger pattern)
                tabIndex={0}
                className="inline-flex w-full"
                data-testid="figma-ticket-upload-new-version-tooltip-trigger"
              >
                <Button
                  variant="solid"
                  size="2"
                  className={
                    !state.selection || !state.ticketId
                      ? "pointer-events-none w-full"
                      : "w-full"
                  }
                  data-testid="figma-ticket-upload-new-version-button"
                  disabled={!state.selection || !state.ticketId}
                  onClick={() => {
                    dispatchState({ type: "ready", payload: {} });
                    requestFigmaSelection();
                  }}
                >
                  {t("tickets.figma-ticket.uploadNewVersion")}
                </Button>
              </span>
            </Tooltip>

            <Flex gap="2" align="center" justify="center">
              <Text size="3" className="text-base-white" align="center" m="2">
                or
              </Text>

              <Button
                size="1"
                variant="outline"
                color="gray"
                className="max-w-max"
                data-testid="figma-ticket-start-new-button"
                onClick={() =>
                  dispatchState({ type: "create_ticket", payload: {} })
                }
              >
                {t("tickets.figma-ticket.startNewTicket")}
              </Button>
            </Flex>
          </Grid>
        );
      }

      if (state.status === "ready") {
        return (
          <Grid gap="2" className="dark mt-8">
            <Text size="3" className="text-base-white">
              {t("tickets.figma-ticket.uploading")}
            </Text>

            <Progress size="3" duration="30s" />
          </Grid>
        );
      }

      if (state.status === "complete") {
        return (
          <Grid gap="2" className="dark">
            <Text size="3" className="text-base-white">
              {t("tickets.figma-ticket.uploadedSuccessfully")}
            </Text>

            <Button
              variant="solid"
              size="2"
              data-testid="figma-ticket-continue-button"
              onClick={() => {
                window.open(state.url, "_blank");
              }}
            >
              {t("tickets.figma-ticket.continuePuntt")}
            </Button>

            <Text size="3" className="text-base-white" align="center" m="2">
              {t("tickets.figma-ticket.or")}
            </Text>

            <Button
              variant="outline"
              size="2"
              data-testid="figma-ticket-start-new-sync-button"
              onClick={() => {
                reset();
                dispatchState({ type: "reset", payload: {} });
              }}
            >
              {t("tickets.figma-ticket.startNewSync")}
            </Button>
          </Grid>
        );
      }
    }

    if (!user) {
      return (
        <Navigate
          to={loginRoute.to}
          search={{ redirect: figmaPluginRoute.to }}
        />
      );
    }

    return (
      <main className="h-max min-h-[200px] bg-grayDark-gray3 p-4">
        <header className="mb-2 flex items-center justify-between">
          <div className="dark">
            <EnterpriseSwitcher
              className="dark"
              allowAddEnterprise={false}
              disabled={state.status === "ready"}
            />
          </div>

          <DropdownMenu.Root>
            <DropdownMenu.Trigger className="cursor-pointer">
              {/* DropdownMenu.Triggers must have exactly one child and apparently it must be block level */}
              <Button size="1" variant="soft" className="p-0">
                <AvatarWithInitials
                  avatar={assetForUser(user?.avatar)}
                  name={user?.name}
                  size={6}
                  square={true}
                />
              </Button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Content className="dark">
              <DropdownMenu.Item
                onClick={() => dispatch(logout(figmaPluginRoute.to))}
                className="cursor-pointer"
              >
                {t("tickets.figma-ticket.logout")}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </header>

        {renderView()}
      </main>
    );
  },
});
